import { useEffect, useRef, useState } from "react";
import { api, API, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Paperclip, At, Checks, X } from "@phosphor-icons/react";

export default function CommentsThread({ pid, user }) {
  const [comments, setComments] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const listRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${pid}/comments`);
      setComments(data.comments);
      await api.post(`/projects/${pid}/comments/read-all`).catch(() => {});
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [pid]);

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [comments.length]);

  const addMention = (u) => {
    if (!mentions.includes(u.id)) setMentions((m) => [...m, u.id]);
    setText((t) => `${t}@${u.name} `.trimStart());
    setShowMentions(false);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post(`/projects/${pid}/comments/attachments`, fd, { timeout: 120000 });
      setAttachments((a) => [...a, data]);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const send = async () => {
    if (!text.trim() && !attachments.length) return;
    setBusy(true);
    try {
      await api.post(`/projects/${pid}/comments`, { text: text.trim(), mentions, attachments });
      setText(""); setMentions([]); setAttachments([]);
      await load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const renderText = (t) => {
    const parts = t.split(/(@\S+(?:\s\S+)?)/g);
    return parts.map((p, i) => p.startsWith("@")
      ? <span key={i} className="text-[var(--c-accent)] font-medium">{p}</span> : p);
  };

  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm" data-testid="comments-thread">
      <div className="px-4 py-2.5 border-b border-[var(--c-border)] flex items-center gap-2">
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--c-muted)">Team comments</span>
        <span className="text-[10px] text-[var(--c-muted)]/70">{comments.length} message{comments.length === 1 ? "" : "s"}</span>
      </div>
      <div ref={listRef} className="max-h-80 overflow-y-auto px-4 py-3 space-y-3">
        {comments.length === 0 && (
          <div className="text-xs text-[var(--c-muted)] py-4 text-center">No comments yet — start the conversation. Use @ to mention a teammate.</div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="group" data-testid={`comment-${c.id}`}>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-[var(--c-text)]">{c.author_name}</span>
              <span className="text-[9px] font-mono text-[var(--c-muted)] uppercase">{c.author_role}</span>
              <span className="text-[10px] text-[var(--c-muted)]">{new Date(c.created_at).toLocaleString()}</span>
              {c.read_by?.length > 0 && (
                <span className="ml-auto flex items-center gap-1 text-[9px] text-[var(--c-muted)]"
                  title={`Seen by ${c.read_by.length}`} data-testid={`comment-seen-${c.id}`}>
                  <Checks size={12} weight="bold" className={c.read_by.length > 1 ? "text-[var(--c-accent)]" : ""} />
                  {c.read_by.length}
                </span>
              )}
            </div>
            {c.text && <div className="text-[13px] text-[var(--c-text)]/90 mt-0.5 whitespace-pre-wrap">{renderText(c.text)}</div>}
            {c.attachments?.map((a, i) => (
              <a key={i} href={`${API}/projects/${pid}/comments/attachment?path=${encodeURIComponent(a.path)}${a.backend ? `&backend=${a.backend}` : ""}`}
                className="inline-flex items-center gap-1 mt-1 mr-2 px-2 py-0.5 bg-[var(--c-border)]/60 rounded-sm text-[10px] font-mono text-[var(--c-muted)] hover:text-[var(--c-accent)]"
                data-testid={`comment-attachment-${c.id}-${i}`}>
                <Paperclip size={10} /> {a.name} <span className="text-[var(--c-muted)]/60">({Math.round(a.size / 1024)} KB)</span>
              </a>
            ))}
          </div>
        ))}
      </div>
      <div className="border-t border-[var(--c-border)] p-3 space-y-2">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((a, i) => (
              <span key={i} className="flex items-center gap-1 px-2 py-0.5 bg-[var(--c-accent)]/10 border border-[var(--c-accent)]/40 rounded-sm text-[10px] text-[var(--c-text)]">
                <Paperclip size={10} /> {a.name}
                <button onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))} className="text-[var(--c-muted)] hover:text-[var(--c-red)]"><X size={10} /></button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
            placeholder="Write a comment… use @ to mention"
            className="w-full bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-3 py-2 text-[13px] text-[var(--c-text)] placeholder:text-[var(--c-muted)]/60 focus:outline-none focus:border-[var(--c-accent)] resize-none"
            data-testid="comment-input"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }} />
          {showMentions && (
            <div className="absolute bottom-full mb-1 left-0 w-64 bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm shadow-xl z-10" data-testid="mention-popover">
              {users.filter((u) => u.id !== user?.id).map((u) => (
                <button key={u.id} onClick={() => addMention(u)} data-testid={`mention-${u.id}`}
                  className="w-full text-left px-3 py-1.5 text-xs text-[var(--c-text)] hover:bg-[var(--c-border)]/50 flex justify-between">
                  <span>{u.name}</span><span className="text-[var(--c-muted)] text-[10px]">{u.role}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowMentions((v) => !v)} data-testid="comment-mention-btn" title="Mention a teammate"
            className="p-1.5 rounded-sm border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-accent)] hover:border-[var(--c-accent)] transition-colors">
            <At size={14} />
          </button>
          <button onClick={() => fileRef.current?.click()} data-testid="comment-attach-btn" title="Attach a file (max 25 MB)"
            className="p-1.5 rounded-sm border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-accent)] hover:border-[var(--c-accent)] transition-colors">
            <Paperclip size={14} />
          </button>
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="comment-file-input" />
          <span className="text-[9px] text-[var(--c-muted)]/60 font-mono">Ctrl+Enter to send</span>
          <button onClick={send} disabled={busy || (!text.trim() && !attachments.length)} data-testid="comment-send"
            className="ml-auto h-7 px-4 rounded-sm bg-[var(--c-accent)] text-white text-xs font-medium hover:bg-[var(--c-accent)]/85 disabled:opacity-40 transition-colors">
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
